import React, { useState, useEffect } from 'react';
import { X, Flame, Wind, User, Target, Clock, Calendar, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  getTodayTotal, getWeekTotal, getMonthTotal, getStreak,
  getDailyGoal, setDailyGoal, getLast30Days, getModeStats, formatDuration
} from '../utils/statsTracker';

const GOAL_OPTIONS = [
  { label: '5 min', value: 300 },
  { label: '10 min', value: 600 },
  { label: '15 min', value: 900 },
  { label: '20 min', value: 1200 },
  { label: '30 min', value: 1800 },
];

const StatsPanel = ({ isOpen, onClose }) => {
  const [todayTotal, setTodayTotal] = useState(0);
  const [weekTotal, setWeekTotal] = useState(0);
  const [monthTotal, setMonthTotal] = useState(0);
  const [streak, setStreak] = useState(0);
  const [goal, setGoal] = useState(600);
  const [last30, setLast30] = useState([]);
  const [modeStats, setModeStats] = useState({ breathing: { count: 0, total: 0 }, bodyscan: { count: 0, total: 0 } });

  useEffect(() => {
    if (!isOpen) return;
    setTodayTotal(getTodayTotal());
    setWeekTotal(getWeekTotal());
    setMonthTotal(getMonthTotal());
    setStreak(getStreak());
    setGoal(getDailyGoal());
    setLast30(getLast30Days());
    setModeStats(getModeStats());
  }, [isOpen]);

  const goalProgress = Math.min((todayTotal / goal) * 100, 100);
  const goalReached = todayTotal >= goal;

  const handleGoalChange = (value) => {
    setGoal(value);
    setDailyGoal(value);
  };

  if (!isOpen) return null;

  const maxDayTotal = Math.max(...last30.map(d => d.total), 1);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="stats-panel">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-slate-900/95 border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 bg-slate-900/95 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-amber-400" />
            Mes statistiques
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors" data-testid="stats-close-btn">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Objectif quotidien */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20" data-testid="daily-goal-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-amber-400" />
                <span className="text-sm font-medium text-white">Objectif du jour</span>
              </div>
              <span className={`text-sm font-bold ${goalReached ? 'text-green-400' : 'text-amber-400'}`}>
                {formatDuration(todayTotal)} / {formatDuration(goal)}
              </span>
            </div>
            {/* Progress bar */}
            <div className="h-3 rounded-full bg-white/10 overflow-hidden mb-3">
              <div
                className={`h-full rounded-full transition-all duration-700 ${goalReached ? 'bg-gradient-to-r from-green-400 to-emerald-500' : 'bg-gradient-to-r from-amber-400 to-orange-500'}`}
                style={{ width: `${goalProgress}%` }}
                data-testid="goal-progress-bar"
              />
            </div>
            {goalReached && (
              <p className="text-green-400 text-xs text-center font-medium" data-testid="goal-reached-msg">
                Objectif atteint ! Bravo !
              </p>
            )}
            {/* Goal selector */}
            <div className="flex gap-1.5 mt-2">
              {GOAL_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleGoalChange(opt.value)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    goal === opt.value
                      ? 'bg-amber-500/30 border border-amber-400/50 text-amber-400'
                      : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`}
                  data-testid={`goal-btn-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Streak + stats rapides */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center" data-testid="streak-card">
              <Flame className={`w-5 h-5 mx-auto mb-1 ${streak > 0 ? 'text-orange-400' : 'text-white/30'}`} />
              <p className="text-xl font-bold text-white">{streak}</p>
              <p className="text-xs text-white/50">jours de suite</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center" data-testid="week-card">
              <Calendar className="w-5 h-5 mx-auto mb-1 text-cyan-400" />
              <p className="text-xl font-bold text-white">{formatDuration(weekTotal)}</p>
              <p className="text-xs text-white/50">cette semaine</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center" data-testid="month-card">
              <Clock className="w-5 h-5 mx-auto mb-1 text-purple-400" />
              <p className="text-xl font-bold text-white">{formatDuration(monthTotal)}</p>
              <p className="text-xs text-white/50">ce mois</p>
            </div>
          </div>

          {/* Par mode */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-white/70">Par mode</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/10 to-cyan-500/10 border border-amber-500/15" data-testid="breathing-stats-card">
                <div className="flex items-center gap-2 mb-2">
                  <Wind className="w-4 h-4 text-amber-400" />
                  <span className="text-sm text-white">Respiration</span>
                </div>
                <p className="text-lg font-bold text-white">{modeStats.breathing.count} <span className="text-xs font-normal text-white/50">séances</span></p>
                <p className="text-xs text-white/50">{formatDuration(modeStats.breathing.total)} au total</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/15" data-testid="bodyscan-stats-card">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-white">Scan Corporel</span>
                </div>
                <p className="text-lg font-bold text-white">{modeStats.bodyscan.count} <span className="text-xs font-normal text-white/50">séances</span></p>
                <p className="text-xs text-white/50">{formatDuration(modeStats.bodyscan.total)} au total</p>
              </div>
            </div>
          </div>

          {/* Calendrier 30 jours */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-white/70">30 derniers jours</h3>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="grid grid-cols-10 gap-1">
                {last30.map((day) => {
                  const intensity = day.total > 0 ? Math.max(0.2, day.total / maxDayTotal) : 0;
                  const d = new Date(day.date);
                  const label = `${d.getDate()}/${d.getMonth() + 1}`;
                  return (
                    <div key={day.date} className="relative group" title={`${label}: ${formatDuration(day.total)}`}>
                      <div
                        className="aspect-square rounded-sm transition-colors"
                        style={{
                          backgroundColor: day.total > 0
                            ? `rgba(245, 158, 11, ${intensity})`
                            : 'rgba(255,255,255,0.05)'
                        }}
                        data-testid={`day-${day.date}`}
                      />
                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded bg-slate-800 text-[10px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        {label} - {day.total > 0 ? formatDuration(day.total) : '0'}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-white/30">
                <span>il y a 30j</span>
                <span>aujourd'hui</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatsPanel;
